import { Controller } from '@hotwired/stimulus'
import $ from 'jquery'
import { debounce } from 'lodash'

const SEARCH_DEBOUNCE_MS = 180
const TAB_INACTIVE = ['tw-bg-transparent', 'tw-border-l-transparent']
const ICON_INACTIVE = ['tw-bg-white', 'tw-text-gray-500', 'tw-border-gray-200']
const NAME_ACTIVE = ['tw-text-gray-900', 'tw-font-semibold']
const NAME_INACTIVE = ['tw-text-gray-700', 'tw-font-medium']
const BADGE_INACTIVE = ['tw-bg-gray-200', 'tw-text-gray-600']
const ROW_COLLAPSED = 'tw-bg-transparent'
const CHEVRON_EXPANDED = 'tw-rotate-0'
const CHEVRON_COLLAPSED = '-tw-rotate-90'

export default class extends Controller {
  static targets = [
    'modal',
    'committedId',
    'selectedBadges',
    'committedBadge',
    'selectedAlert',
    'emptyWarning',
    'mobileSectorSelect',
    'mobileSectorOption',
    'sectorPanel',
    'sectorTab',
    'sectorTabIcon',
    'sectorTabName',
    'sectorMasterCheckbox',
    'middleContainer',
    'middleRow',
    'middleMasterCheckbox',
    'middleBody',
    'middleCountBadge',
    'smallRow',
    'smallCheckbox',
    'accordionToggle',
    'sectorCountBadge',
    'totalCount',
    'commitCount',
    'commitButton',
    'limitWarning',
    'search',
    'searchEmpty',
  ]
  static values = { initialSectorId: String, inline: String, maxCount: Number }
  static classes = ['tabActive', 'iconActive', 'iconWithSelection', 'badgeActive', 'rowExpanded']

  declare readonly modalTarget: HTMLElement
  declare readonly hasModalTarget: boolean
  declare readonly committedIdTargets: HTMLInputElement[]
  declare readonly selectedBadgesTarget: HTMLElement
  declare readonly committedBadgeTargets: HTMLButtonElement[]
  declare readonly selectedAlertTarget: HTMLElement
  declare readonly emptyWarningTarget: HTMLElement
  declare readonly mobileSectorSelectTarget: HTMLSelectElement
  declare readonly mobileSectorOptionTargets: HTMLOptionElement[]
  declare readonly sectorPanelTargets: HTMLElement[]
  declare readonly sectorTabTargets: HTMLButtonElement[]
  declare readonly sectorTabIconTargets: HTMLElement[]
  declare readonly sectorTabNameTargets: HTMLElement[]
  declare readonly sectorMasterCheckboxTargets: HTMLInputElement[]
  declare readonly middleContainerTargets: HTMLElement[]
  declare readonly middleRowTargets: HTMLElement[]
  declare readonly middleMasterCheckboxTargets: HTMLInputElement[]
  declare readonly middleBodyTargets: HTMLElement[]
  declare readonly middleCountBadgeTargets: HTMLElement[]
  declare readonly smallRowTargets: HTMLLabelElement[]
  declare readonly smallCheckboxTargets: HTMLInputElement[]
  declare readonly accordionToggleTargets: HTMLButtonElement[]
  declare readonly sectorCountBadgeTargets: HTMLElement[]
  declare readonly totalCountTarget: HTMLElement
  declare readonly hasTotalCountTarget: boolean
  declare readonly commitCountTarget: HTMLElement
  declare readonly hasCommitCountTarget: boolean
  declare readonly commitButtonTarget: HTMLButtonElement
  declare readonly limitWarningTarget: HTMLElement
  declare readonly hasCommitButtonTarget: boolean
  declare readonly hasLimitWarningTarget: boolean
  declare readonly searchTarget: HTMLInputElement
  declare readonly hasSearchTarget: boolean
  declare readonly searchEmptyTarget: HTMLElement
  declare readonly hasSearchEmptyTarget: boolean
  declare readonly initialSectorIdValue: string
  declare readonly maxCountValue: number
  declare readonly tabActiveClasses: string[]
  declare readonly iconActiveClasses: string[]
  declare readonly iconWithSelectionClasses: string[]
  declare readonly badgeActiveClasses: string[]
  declare readonly rowExpandedClass: string
  declare readonly inlineValue: string

  // モーダルを開いた瞬間の「確定済みID集合」。cancel 時はここへ復元する。
  private snapshot: Set<string> = new Set()
  // modal内で「選択→選択する」経由でmodalを閉じたかどうか。
  // もしescキーやキャンセルボタンで閉じた場合に再度モーダルを開いた際、選択済みの業界を復元するために使用する
  private committing = false
  private activeSectorId: string | null = null
  private modalHiddenHandler?: () => void

  private searchQuery = ''
  // IME変換中に検索処理が走らないようにtrue の間は debounce 後の再計算をスキップする。
  private isComposing = false
  private debouncedApplyFilter = debounce((): void => {
    if (this.isComposing) return
    this.applyFilter()
  }, SEARCH_DEBOUNCE_MS)

  connect(): void {
    if (this.hasModalTarget) {
      this.modalHiddenHandler = this.handleHidden.bind(this)
      $(this.modalTarget).on('hidden.bs.modal', this.modalHiddenHandler)
    }
    this.activeSectorId = this.initialSectorIdValue || this.sectorPanelTargets[0]?.dataset.sectorId || null
    this.recomputeAll()
  }

  disconnect(): void {
    if (this.modalHiddenHandler && this.hasModalTarget) {
      $(this.modalTarget).off('hidden.bs.modal', this.modalHiddenHandler)
    }
    this.debouncedApplyFilter.cancel()
  }

  openModal(): void {
    if (!this.hasModalTarget) return
    this.clearFilter()
    this.snapshot = this.currentCommittedIds()
    this.syncCheckboxesToCommitted()
    this.recomputeAll()
    this.activateInitialSector()
    $(this.modalTarget).modal('show')
  }

  selectSectorTab(event: Event): void {
    const target = event.currentTarget as HTMLElement
    const sectorId = target.dataset.sectorId
    if (!sectorId) return
    this.showSectorPanel(sectorId)
  }

  selectSectorFromDropdown(event: Event): void {
    const select = event.currentTarget as HTMLSelectElement
    this.showSectorPanel(select.value)
  }

  toggleAccordion(event: Event): void {
    // NOTE: ラベル/チェックボックス領域のクリックはチェック操作に譲る
    const origin = event.target as HTMLElement
    if (origin.closest('input, label')) return

    const current = event.currentTarget as HTMLElement
    const middleId = current.dataset.middleId
    if (!middleId) return
    const body = this.middleBodyTargets.find((el) => el.dataset.middleId === middleId)
    body?.classList.toggle('d-none')
    const expanded = body ? !body.classList.contains('d-none') : false

    const row = this.middleRowTargets.find((el) => el.dataset.middleId === middleId)
    if (row) {
      row.classList.toggle(this.rowExpandedClass, expanded)
      row.classList.toggle(ROW_COLLAPSED, !expanded)
    }
    const toggle = this.accordionToggleTargets.find((el) => el.dataset.middleId === middleId)
    if (toggle) {
      toggle.classList.toggle(CHEVRON_EXPANDED, expanded)
      toggle.classList.toggle(CHEVRON_COLLAPSED, !expanded)
    }
  }

  // NOTE: 小分類チェックボックスの変更ハンドラ。親 middle / sector の集計と合計を再計算。
  onSmallCheckboxChange(event: Event): void {
    const checkbox = event.currentTarget as HTMLInputElement
    this.syncAliasCheckboxesAndRecompute([checkbox])
    this.updateTotals()
    if (this.isInline) this.syncCommittedFromCheckboxes()
  }

  toggleMiddleAll(event: Event): void {
    const checkbox = event.currentTarget as HTMLInputElement
    const middleId = checkbox.dataset.middleId
    if (!middleId) return

    // NOTE: フィルタ適用中は表示中（ヒット済み）の小分類だけを対象にする。
    const smalls = this.effectiveSmallsForMiddle(middleId)
    const nextValue = checkbox.checked
    smalls.forEach((cb) => (cb.checked = nextValue))

    this.syncAliasCheckboxesAndRecompute(smalls)
    // 検索時にセクターに表示中の行が残っていない場合は syncAliasCheckboxesAndRecompute が再計算しないため、マスター状態だけ明示的に更新する。
    if (smalls.length === 0) this.recomputeMiddle(middleId)
    this.updateTotals()
    if (this.isInline) this.syncCommittedFromCheckboxes()
  }

  toggleSectorAll(event: Event): void {
    const checkbox = event.currentTarget as HTMLInputElement
    const sectorId = checkbox.dataset.sectorId
    if (!sectorId) return

    // NOTE: フィルタ適用中は表示中（ヒット済み）の小分類だけを対象にする。
    const smalls = this.effectiveSmallsForSector(sectorId)
    const nextValue = checkbox.checked
    smalls.forEach((cb) => (cb.checked = nextValue))

    this.syncAliasCheckboxesAndRecompute(smalls)
    // 検索時にセクターに表示中の行が残っていない場合は syncAliasCheckboxesAndRecompute が再計算しないため、マスター状態だけ明示的に更新する。
    if (smalls.length === 0) this.recomputeSector(sectorId)
    this.updateTotals()
    if (this.isInline) this.syncCommittedFromCheckboxes()
  }

  clearAll(): void {
    this.smallCheckboxTargets.forEach((cb) => (cb.checked = false))
    this.recomputeAll()
    if (this.isInline) this.syncCommittedFromCheckboxes()
  }

  commit(): void {
    this.syncCommittedFromCheckboxes()
    if (this.hasModalTarget) {
      this.committing = true // NOTE: フラグを立ててモーダルを閉じても、復元されないようにする
      $(this.modalTarget).modal('hide')
    }
  }

  // NOTE: 現在の checkbox 状態を hidden_field（送信対象）と badge の表示状態に反映する。
  private syncCommittedFromCheckboxes(): void {
    const checkedIds = new Set(
      this.smallCheckboxTargets.filter((cb) => cb.checked).map((cb) => cb.value)
    )
    this.committedIdTargets.forEach((hidden) => {
      hidden.disabled = !checkedIds.has(hidden.value)
    })
    this.committedBadgeTargets.forEach((badge) => {
      const id = badge.dataset.smallSectorId
      badge.classList.toggle('d-none', !(id && checkedIds.has(id)))
    })
    this.updateEmptyWarning()
    // NOTE: hidden_field の disabled 切り替えでは change イベントが発火しないため、確定を外部へ通知する
    this.dispatch('changed')
  }

  private handleHidden(): void {
    this.clearFilter()
    if (this.committing) {
      this.committing = false
      return
    }
    this.syncCheckboxesToCommitted()
    this.recomputeAll()
  }

  deselectFromBadge(event: Event): void {
    const badge = event.currentTarget as HTMLButtonElement
    const id = badge.dataset.smallSectorId
    if (!id) return
    const hidden = this.committedIdTargets.find((h) => h.value === id)
    if (hidden) hidden.disabled = true
    badge.classList.add('d-none')
    this.updateEmptyWarning()
    this.dispatch('changed')
  }

  private currentCommittedIds(): Set<string> {
    return new Set(this.committedIdTargets.filter((h) => !h.disabled).map((h) => h.value))
  }

  private syncCheckboxesToCommitted(): void {
    this.smallCheckboxTargets.forEach((cb) => {
      cb.checked = this.snapshot.has(cb.value)
    })
  }

  private recomputeAll(): void {
    const middleIds = new Set(
      this.middleMasterCheckboxTargets.map((cb) => cb.dataset.middleId).filter(Boolean) as string[]
    )
    middleIds.forEach((middleId) => this.recomputeMiddle(middleId))

    const sectorIds = new Set(
      this.sectorMasterCheckboxTargets.map((cb) => cb.dataset.sectorId).filter(Boolean) as string[]
    )
    sectorIds.forEach((sectorId) => this.recomputeSector(sectorId))

    this.updateTotals()
  }

  // NOTE: 同一 small_sector_id のチェックボックスが複数（メイン + サブ参照）描画されるため、
  // 状態が変わったチェックボックスと同じ small_sector_id を持つ他のチェックボックスへ checked 状態を反映し、
  // 影響を受けた middle / sector を重複なく再計算する。
  private syncAliasCheckboxesAndRecompute(changed: HTMLInputElement[]): void {
    const checkedBySmallSectorId = new Map<string, boolean>()
    changed.forEach((cb) => {
      if (cb.dataset.smallSectorId) checkedBySmallSectorId.set(cb.dataset.smallSectorId, cb.checked)
    })
    const affected = this.smallCheckboxTargets.filter((cb) => {
      const id = cb.dataset.smallSectorId
      if (!id || !checkedBySmallSectorId.has(id)) return false
      cb.checked = checkedBySmallSectorId.get(id) as boolean
      return true
    })
    const middleIds = new Set(affected.map((cb) => cb.dataset.middleId).filter(Boolean) as string[])
    const sectorIds = new Set(affected.map((cb) => cb.dataset.sectorId).filter(Boolean) as string[])
    middleIds.forEach((middleId) => this.recomputeMiddle(middleId))
    sectorIds.forEach((sectorId) => this.recomputeSector(sectorId))
  }

  private countDistinctSmallSectors(checkboxes: HTMLInputElement[]): number {
    return new Set(checkboxes.map((cb) => cb.dataset.smallSectorId)).size
  }

  private recomputeMiddle(middleId: string): void {
    const smalls = this.effectiveSmallsForMiddle(middleId)
    const checkedCount = smalls.filter((cb) => cb.checked).length
    const allChecked = smalls.length > 0 && checkedCount === smalls.length
    const partial = checkedCount > 0 && !allChecked

    const master = this.middleMasterCheckboxTargets.find((cb) => cb.dataset.middleId === middleId)
    if (master) {
      master.checked = allChecked
      master.indeterminate = partial
    }

    const badge = this.middleCountBadgeTargets.find((el) => el.dataset.middleId === middleId)
    if (badge) {
      if (checkedCount > 0) {
        badge.textContent = `${checkedCount}/${smalls.length}`
        badge.classList.remove('d-none')
      } else {
        badge.textContent = ''
        badge.classList.add('d-none')
      }
    }
  }

  private recomputeSector(sectorId: string): void {
    const allSmalls = this.smallCheckboxesForSector(sectorId)
    // メインとサブ参照が同一 sector 内に並ぶ場合の二重カウントを避けるため small_sector_id で重複排除する。
    const totalCheckedCount = this.countDistinctSmallSectors(allSmalls.filter((cb) => cb.checked))

    const effectiveSmalls = this.effectiveSmallsForSector(sectorId)
    const effectiveCheckedCount = effectiveSmalls.filter((cb) => cb.checked).length
    const allChecked = effectiveSmalls.length > 0 && effectiveCheckedCount === effectiveSmalls.length
    const partial = effectiveCheckedCount > 0 && !allChecked

    const master = this.sectorMasterCheckboxTargets.find((cb) => cb.dataset.sectorId === sectorId)
    if (master) {
      master.checked = allChecked
      master.indeterminate = partial
    }

    this.applyTabStyles(sectorId, sectorId === this.activeSectorId, totalCheckedCount > 0)

    const badge = this.sectorCountBadgeTargets.find((el) => el.dataset.sectorId === sectorId)
    if (badge) {
      if (totalCheckedCount > 0) {
        badge.textContent = String(totalCheckedCount)
        badge.classList.remove('d-none')
      } else {
        badge.textContent = ''
        badge.classList.add('d-none')
      }
    }
  }

  private updateTotals(): void {
    const total = this.countDistinctSmallSectors(this.smallCheckboxTargets.filter((cb) => cb.checked))
    if (this.hasTotalCountTarget) this.totalCountTarget.textContent = String(total)
    if (this.hasCommitCountTarget) this.commitCountTarget.textContent = String(total)
    this.updateLimitState(total)
  }

  // NOTE: 上限を超えた場合は「選択する」ボタンを無効化し、警告メッセージを表示する。
  private updateLimitState(total: number): void {
    const isOverLimit = total > this.maxCountValue
    if (this.hasCommitButtonTarget) {
      this.commitButtonTarget.disabled = isOverLimit
    }
    if (this.hasLimitWarningTarget) {
      this.limitWarningTarget.classList.toggle('d-none', !isOverLimit)
    }
  }

  private smallCheckboxesForMiddle(middleId: string): HTMLInputElement[] {
    return this.smallCheckboxTargets.filter((cb) => cb.dataset.middleId === middleId)
  }

  private smallCheckboxesForSector(sectorId: string): HTMLInputElement[] {
    return this.smallCheckboxTargets.filter((cb) => cb.dataset.sectorId === sectorId)
  }

  private updateEmptyWarning(): void {
    const anyEnabled = this.committedIdTargets.some((h) => !h.disabled)
    this.emptyWarningTarget.classList.toggle('d-none', anyEnabled)
    this.selectedAlertTarget.classList.toggle('d-none', !anyEnabled)
  }

  private activateInitialSector(): void {
    const initialSectorId = this.computeInitialSectorId(this.snapshot)
    if (!initialSectorId) return
    this.showSectorPanel(initialSectorId)
  }

  private computeInitialSectorId(committedIds: Set<string>): string | null {
    if (committedIds.size === 0) {
      return this.sectorPanelTargets[0]?.dataset.sectorId ?? null
    }

    const panelWithSelection = this.sectorPanelTargets.find((panel) => {
      const sectorId = panel.dataset.sectorId
      if (!sectorId) return false
      return this.smallCheckboxesForSector(sectorId).some((cb) => committedIds.has(cb.value))
    })
    return (panelWithSelection ?? this.sectorPanelTargets[0])?.dataset.sectorId ?? null
  }

  private showSectorPanel(sectorId: string): void {
    this.activeSectorId = sectorId
    this.sectorPanelTargets.forEach((panel) => {
      panel.classList.toggle('d-none', panel.dataset.sectorId !== sectorId)
    })
    this.sectorTabTargets.forEach((tab) => {
      const tabSectorId = tab.dataset.sectorId
      if (!tabSectorId) return
      const active = tabSectorId === sectorId
      const hasSelection = this.smallCheckboxesForSector(tabSectorId).some((cb) => cb.checked)
      this.applyTabStyles(tabSectorId, active, hasSelection)
    })
    this.mobileSectorSelectTarget.value = sectorId
  }

  // NOTE: 左ペインタブの 4 要素（行 / アイコン / 名前 / バッジ）に、
  //  active（現在開いているタブ）/ has-selection（選択あり）/ inactive
  // の 3 状態に応じた Tailwind クラスセットを付け替える。
  private applyTabStyles(sectorId: string, active: boolean, hasSelection: boolean): void {
    const tab = this.sectorTabTargets.find((t) => t.dataset.sectorId === sectorId)
    if (tab) replaceClasses(tab, TAB_INACTIVE, this.tabActiveClasses, active)

    const icon = this.sectorTabIconTargets.find((el) => el.dataset.sectorId === sectorId)
    if (icon) {
      icon.classList.remove(...this.iconActiveClasses, ...this.iconWithSelectionClasses, ...ICON_INACTIVE)
      if (active) icon.classList.add(...this.iconActiveClasses)
      else if (hasSelection) icon.classList.add(...this.iconWithSelectionClasses)
      else icon.classList.add(...ICON_INACTIVE)
    }
    const name = this.sectorTabNameTargets.find((el) => el.dataset.sectorId === sectorId)
    if (name) replaceClasses(name, NAME_INACTIVE, NAME_ACTIVE, active)

    const badge = this.sectorCountBadgeTargets.find((el) => el.dataset.sectorId === sectorId)
    if (badge) replaceClasses(badge, BADGE_INACTIVE, this.badgeActiveClasses, active)
  }

  applyFilterDebounced(): void {
    this.debouncedApplyFilter()
  }

  suspendFilterForIme(): void {
    this.isComposing = true
  }

  applyFilterAfterIme(): void {
    this.isComposing = false
    this.debouncedApplyFilter.cancel()
    this.applyFilter()
  }

  private applyFilter(): void {
    if (!this.hasSearchTarget) return
    const query = this.searchTarget.value.trim().toLowerCase()
    this.searchQuery = query

    if (query === '') {
      this.clearFilterVisibility()
      this.recomputeAll()
      return
    }

    const visibleMiddleIds = new Set<string>()
    const visibleSectorIds = new Set<string>()
    this.smallRowTargets.forEach((row) => {
      const matched = (row.dataset.filterText ?? '').includes(query)
      row.classList.toggle('d-none', !matched)
      if (!matched) return
      const { middleId, sectorId } = row.dataset
      if (middleId) visibleMiddleIds.add(middleId)
      if (sectorId) visibleSectorIds.add(sectorId)
    })

    this.middleContainerTargets.forEach((container) => {
      const middleId = container.dataset.middleId
      const hasHit = !!middleId && visibleMiddleIds.has(middleId)
      container.classList.toggle('d-none', !hasHit)
      if (hasHit) this.expandMiddle(middleId)
    })

    this.sectorTabTargets.forEach((tab) => {
      const sectorId = tab.dataset.sectorId
      const hasHit = !!sectorId && visibleSectorIds.has(sectorId)
      tab.classList.toggle('d-none', !hasHit)
    })

    this.mobileSectorOptionTargets.forEach((option) => {
      const sectorId = option.dataset.sectorId
      const hasHit = !!sectorId && visibleSectorIds.has(sectorId)
      option.disabled = !hasHit
      option.classList.toggle('d-none', !hasHit)
    })

    const allEmpty = visibleSectorIds.size === 0
    if (this.hasSearchEmptyTarget) {
      this.searchEmptyTarget.classList.toggle('d-none', !allEmpty)
    }

    if (allEmpty) {
      this.sectorPanelTargets.forEach((panel) => panel.classList.add('d-none'))
    } else {
      const nextSectorId = this.activeSectorId && visibleSectorIds.has(this.activeSectorId)
        ? this.activeSectorId
        : this.sectorPanelTargets.map((p) => p.dataset.sectorId).find((id): id is string => !!id && visibleSectorIds.has(id))
      if (nextSectorId) this.showSectorPanel(nextSectorId)
    }

    this.recomputeAll()
  }

  private clearFilterVisibility(): void {
    this.smallRowTargets.forEach((row) => row.classList.remove('d-none'))
    this.middleContainerTargets.forEach((c) => c.classList.remove('d-none'))
    this.sectorTabTargets.forEach((t) => t.classList.remove('d-none'))
    this.mobileSectorOptionTargets.forEach((o) => {
      o.disabled = false
      o.classList.remove('d-none')
    })
    if (this.hasSearchEmptyTarget) this.searchEmptyTarget.classList.add('d-none')
    if (this.activeSectorId) this.showSectorPanel(this.activeSectorId)
  }

  private clearFilter(): void {
    this.debouncedApplyFilter.cancel()
    this.isComposing = false
    this.searchQuery = ''
    if (this.hasSearchTarget) this.searchTarget.value = ''
    this.clearFilterVisibility()
  }

  private isFiltering(): boolean {
    return this.searchQuery !== ''
  }

  // インライン表示モード（modal を介さず常時展開・リアルタイム反映）。
  private get isInline(): boolean {
    return this.inlineValue === 'true'
  }

  private effectiveSmallsForMiddle(middleId: string): HTMLInputElement[] {
    return this.effectiveSmalls('middleId', middleId)
  }

  private effectiveSmallsForSector(sectorId: string): HTMLInputElement[] {
    return this.effectiveSmalls('sectorId', sectorId)
  }

  private effectiveSmalls(dataKey: 'middleId' | 'sectorId', id: string): HTMLInputElement[] {
    const scoped = this.smallCheckboxTargets.filter((cb) => cb.dataset[dataKey] === id)
    if (!this.isFiltering()) return scoped
    const visibleSmallIds = new Set(
      this.smallRowTargets
        .filter((row) => !row.classList.contains('d-none'))
        .map((row) => row.dataset.smallSectorId)
    )
    return scoped.filter((cb) => visibleSmallIds.has(cb.value))
  }

  private expandMiddle(middleId: string): void {
    const body = this.middleBodyTargets.find((el) => el.dataset.middleId === middleId)
    body?.classList.remove('d-none')
    const row = this.middleRowTargets.find((el) => el.dataset.middleId === middleId)
    if (row) {
      row.classList.add(this.rowExpandedClass)
      row.classList.remove(ROW_COLLAPSED)
    }
    const toggle = this.accordionToggleTargets.find((el) => el.dataset.middleId === middleId)
    if (toggle) {
      toggle.classList.add(CHEVRON_EXPANDED)
      toggle.classList.remove(CHEVRON_COLLAPSED)
    }
  }
}

function replaceClasses(el: HTMLElement, offClasses: readonly string[], onClasses: readonly string[], on: boolean): void {
  const [add, remove] = on ? [onClasses, offClasses] : [offClasses, onClasses]
  el.classList.remove(...remove)
  el.classList.add(...add)
}
